<script lang="ts">import { Button } from "@infinitro/button";
import { cfgToThemeCdnPath } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { handleIntercom, ModalDialog, modalStack, popModal } from "@infinitro/global";
import { WarningNewIcon } from "@infinitro/icons";
import { Text } from "@infinitro/text";
import { derived } from "svelte/store";
import Modal from "../modal.svelte";
const warningIconUrl = cfgToThemeCdnPath(`/icons/warning-icon.png`);
const thisModal = ModalDialog.Error;
const modalIndex = derived(modalStack, ($modalStack) => $modalStack.findIndex((e) => e.modal === thisModal));
const isModalOpen = derived(modalIndex, ($modalIndex) => $modalIndex >= 0);
const payload = derived(modalStack, ($modalStack) => $modalStack.find((e) => e.modal === thisModal)?.data);
const zOverlay = derived(modalIndex, ($modalIndex) => $modalIndex >= 0 ? 9999 + $modalIndex * 2 : 9999);
const closeModal = () => popModal(thisModal);
let errors = new Set();
function addError(message) {
	errors.clear();
	errors.add(message);
	errors = new Set(errors);
}
function clearErrors() {
	errors.clear();
	errors = new Set(errors);
}
const handleRightButtonClick = async () => {
	try {
		if ($payload?.onRightButtonClick) {
			const [pass, errorMessage] = await $payload.onRightButtonClick();
			if (pass === false) {
				addError(errorMessage ?? "unknown error");
				return;
			}
		}
		closeModal();
	} catch (e) {
		console.error("error handling right button click", e);
		addError(e);
	}
};
const handleLeftButtonClick = async () => {
	try {
		if ($payload?.onLeftButtonClick) {
			const [pass, errorMessage] = await $payload.onLeftButtonClick();
			if (pass === false) {
				addError(errorMessage ?? "unknown error");
				return;
			}
		}
		closeModal();
	} catch (e) {
		console.error("error handling left button click", e);
		addError(e);
	}
};
const handleClose = async () => {
	try {
		if ($payload?.onClose) {
			const pass = await $payload.onClose();
			if (pass === false) return;
		}
		closeModal();
	} catch (e) {
		console.error("error handling close", e);
		addError(e);
	}
};
</script>

{#if isModalOpen}
	<Modal
		closeModal={handleClose}
		zIndexOverlay={$zOverlay}
		zIndexUnderlay={$zOverlay - 1}
		width="100%"
		maxWidth="400px"
		maxHeight="90vh"
		notFullScreenMobile={true}
		noAnimation
		alwaysCentered
	>
		<div class="confirmation-content">
			<div class="actions"></div>
		</div>
		<div class="content">
			<img src={warningIconUrl} alt={$t(Verbiage.error_alt, 'alts')} class="error-icon" />

			{#if errors.size > 0}
				<div class="error-box">
					<WarningNewIcon />
					<small class="withdraw-disclaimer">
						<strong>{`${$t(Verbiage.error)}:`}</strong>
						<span>{Array.from(errors).join(' ')}</span>
					</small>
				</div>
			{/if}

			<Text type="title" titleSize="h4" textColor="white" bodySize="small" textAlign={'center'}>
				{#if $payload?.title}
					{$payload.title}
				{:else}
					{$t(Verbiage.error_title)}
				{/if}
			</Text>

			<Text type="body" titleSize="h5" textColor="gray" bodySize="small" textAlign={'center'}>
				{#if $payload?.content}
					{$payload.content}
				{:else}
					{$t(Verbiage.error_body)}
				{/if}
			</Text>

			<div class="buttons">
				{#if $payload?.showIntercom}
					<Button
						onClick={handleIntercom}
						size="medium"
						text={$t(Verbiage.contact_support)}
						variant="primary"
					/>
				{/if}
				{#if $payload?.showRefreshPage}
					<Button
						size="medium"
						text={$t(Verbiage.refresh_page)}
						variant="tertiary"
						onClick={() => window.location.reload()}
					/>
				{/if}
				{#if $payload?.leftButtonText || (!$payload?.showIntercom && !$payload?.showRefreshPage)}
					<Button
						variant={$payload?.leftButtonVariant ?? 'tertiary'}
						text={$payload?.leftButtonText ?? $t(Verbiage.close)}
						onClick={handleLeftButtonClick}
						size={$payload?.rightButtonText || $payload?.showIntercom || $payload?.showRefreshPage
							? 'medium'
							: 'large'}
						fullWidth
					/>
				{/if}
				{#if $payload?.rightButtonText}
					<Button
						variant={$payload?.rightButtonVariant ?? 'primary'}
						text={$payload?.rightButtonText}
						onClick={handleRightButtonClick}
						size="medium"
						fullWidth
					/>
				{/if}
			</div>
		</div>
	</Modal>
{/if}

<style>
	.content {
		display: flex;
		flex-direction: column;
		gap: 1.5rem;
}

	.description {
		margin: 0px;
		font-size: 0.875rem;
		line-height: 1.25rem;
		color: var(--color-text-100);
}

	.actions {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 0.75rem;
}

	.content {
		display: flex;
		height: 100%;
		width: 100%;
		flex-direction: column;
		align-items: center;
		gap: 1rem;
		padding: 1rem;
		text-align: center;
}

	.buttons {
		margin-top: 1rem;
		display: flex;
		justify-content: center;
		gap: 1rem;
		padding-left: 0.5rem;
		padding-right: 0.5rem;
}

	.error-icon {
		width: 172px;
		height: 172px;
	}

	@media (min-width: 640px) {
		.content {
				gap: 0.5rem;
				padding: 0.5rem;
		}

		.error-icon {
			width: 120px;
			height: 120px;
		}

		.buttons {
				gap: 0.5rem;
		}
	}

	@media (min-width: 768px) {
		.content {
				gap: 0.25rem;
				padding: 0.25rem;
		}

		.error-icon {
			width: 100px;
			height: 100px;
		}

		.buttons {
				gap: 0.25rem;
		}
	}

	.error-box {
		display: flex;
		width: 100%;
		align-items: center;
		gap: 0.5rem;
		border-radius: 0.5rem;
		background-color: var(--component-error-background);
		padding: 0.5rem;
		text-align: start;
		color: var(--component-error-text);
}

	.error-box :global(svg) {
		color: inherit;
		flex-shrink: 0;
	}

	.withdraw-disclaimer {
		font-size: 0.75rem;
		line-height: 1rem;
		color: inherit;
}
</style>
